import {defineMessages, type MessageDescriptor} from 'react-intl'
import {type OfferTitleKey} from '@findhotel/sapi'

export const DEFAULT_OFFER_TITLE_KEY: OfferTitleKey = 'room_only_non_refundable'

/**
 * Maps SAPI `offerTitleKey` values to display messages.
 *
 * Each key follows the pattern `{meal}_{cancellation}` where:
 * - meal: room_only | breakfast | half_board | full_board | all_inclusive
 * - cancellation: non_refundable | free_cancellation | partially_refundable
 *
 * The key is provided by SAPI on each offer and determines the offer card title
 * shown to the user (e.g. "Breakfast + Free cancellation").
 */
export const offerTitleMessages: Record<OfferTitleKey, MessageDescriptor> =
  defineMessages({
    room_only_non_refundable: {
      id: 'roomOfferCardTitle.basic',
      defaultMessage: 'Basic'
    },
    room_only_free_cancellation: {
      id: 'cancellationPenalties.freeCancellation',
      defaultMessage: 'Free cancellation'
    },
    room_only_partially_refundable: {
      id: 'roomOfferCardTitle.partiallyRefundable',
      defaultMessage: 'Partially refundable'
    },
    breakfast_non_refundable: {
      id: 'mealType.breakfastIncluded',
      defaultMessage: 'Breakfast included'
    },
    breakfast_free_cancellation: {
      id: 'roomOfferCardTitle.breakfastAndFreeCancellation',
      defaultMessage: 'Breakfast + Free cancellation'
    },
    breakfast_partially_refundable: {
      id: 'roomOfferCardTitle.breakfastAndPartiallyRefundable',
      defaultMessage: 'Breakfast + Partially refundable'
    },
    half_board_non_refundable: {
      id: 'roomOfferCardTitle.halfBoard',
      defaultMessage: 'Half board'
    },
    half_board_free_cancellation: {
      id: 'roomOfferCardTitle.halfBoardAndFreeCancellation',
      defaultMessage: 'Half board + Free cancellation'
    },
    half_board_partially_refundable: {
      id: 'roomOfferCardTitle.halfBoard',
      defaultMessage: 'Half board'
    },
    full_board_non_refundable: {
      id: 'roomOfferCardTitle.fullBoard',
      defaultMessage: 'Full board'
    },
    full_board_free_cancellation: {
      id: 'roomOfferCardTitle.fullBoardAndFreeCancellation',
      defaultMessage: 'Full board + Free cancellation'
    },
    full_board_partially_refundable: {
      id: 'roomOfferCardTitle.fullBoard',
      defaultMessage: 'Full board'
    },
    all_inclusive_non_refundable: {
      id: 'mealType.allInclusive',
      defaultMessage: 'All inclusive'
    },
    all_inclusive_free_cancellation: {
      id: 'roomOfferCardTitle.allInclusiveAndFreeCancellation',
      defaultMessage: 'All inclusive + Free cancellation'
    },
    all_inclusive_partially_refundable: {
      id: 'mealType.allInclusive',
      defaultMessage: 'All inclusive'
    }
  })

export const getOfferTitleMessage = (
  offerTitleKey: OfferTitleKey | string | undefined
): MessageDescriptor | undefined => {
  if (!offerTitleKey) return undefined
  return offerTitleMessages[offerTitleKey as OfferTitleKey]
}

// BoVio sends snake_case, SAPI camelCase (sc-189001); accept both.
const BOARD_TYPE_NORMALIZATION = {
  room_only: 'room_only',
  roomOnly: 'room_only',
  breakfast: 'breakfast',
  lunch: 'lunch',
  dinner: 'dinner',
  half_board: 'half_board',
  halfBoard: 'half_board',
  full_board: 'full_board',
  fullBoard: 'full_board',
  all_inclusive: 'all_inclusive',
  allInclusive: 'all_inclusive'
} as const

type NormalizedBoardType =
  (typeof BOARD_TYPE_NORMALIZATION)[keyof typeof BOARD_TYPE_NORMALIZATION]

/**
 * Canonical snake_case board type for a raw `boardType`. Missing boardType
 * means room_only; unrecognized values return undefined.
 */
export const normalizeBoardType = (
  boardType?: string
): NormalizedBoardType | undefined =>
  boardType === undefined
    ? 'room_only'
    : BOARD_TYPE_NORMALIZATION[
        boardType as keyof typeof BOARD_TYPE_NORMALIZATION
      ]

// Meals an OfferTitleKey can express — lunch/dinner have no title-key segment
const TITLE_KEY_MEALS = new Set<NormalizedBoardType>([
  'room_only',
  'breakfast',
  'half_board',
  'full_board',
  'all_inclusive'
])

/**
 * Meal segment of an `OfferTitleKey` for a raw `boardType`, or undefined when
 * the board type can't be expressed in a title key.
 */
export const boardTypeToMeal = (boardType?: string) => {
  const normalized = normalizeBoardType(boardType)
  return normalized && TITLE_KEY_MEALS.has(normalized) ? normalized : undefined
}

/**
 * Maps a raw `boardType` to the free-cancellation `OfferTitleKey`, so
 * board + FC labels reuse the `offerTitleMessages` copy.
 */
export const boardTypeToFreeCancellationTitleKey = (
  boardType?: string
): OfferTitleKey =>
  `${boardTypeToMeal(boardType) ?? 'room_only'}_free_cancellation` as OfferTitleKey
